import {
  Survey,
  SurveyDeliveryMode,
  SurveyQuestion,
  SurveyQuestionType,
  SurveyRecurringDays,
  SurveyStatus,
  SurveyWhen,
  SurveyDeploymentGroup,
  SurveyPaceMode,
  SurveyAudienceMode,
  SurveyMobilePlatform,
} from '../../types';

export interface SurveyFormState {
  name: string;
  summary: string;
  status: SurveyStatus;
  deliveryMode: SurveyDeliveryMode;
  when: SurveyWhen;
  recurringDays: SurveyRecurringDays;
  audienceMode: SurveyAudienceMode;
  deploymentGroups: SurveyDeploymentGroup[];
  deviceModels: string[];
  mobilePlatforms: SurveyMobilePlatform[];
  maxResponses: string;
  maxImpressions: string;
  paceMode: SurveyPaceMode;
  perDay: string;
  questions: SurveyQuestion[];
}

export const SURVEY_STATUS_OPTIONS: { value: SurveyStatus; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
];

export const SURVEY_QUESTION_TYPE_OPTIONS: { value: SurveyQuestionType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'choice', label: 'Multiple choice' },
];

export const SURVEY_RECURRING_DAY_OPTIONS: SurveyRecurringDays[] = [1, 5, 15, 30, 60, 90];

const normalizeRecurringDays = (days?: number): SurveyRecurringDays => (
  SURVEY_RECURRING_DAY_OPTIONS.includes(days as SurveyRecurringDays)
    ? days as SurveyRecurringDays
    : 30
);

export const SURVEY_MOBILE_PLATFORM_OPTIONS: { value: SurveyMobilePlatform; label: string }[] = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

export const normalizeMobilePlatforms = (platforms?: string[]) => {
  const selected = [...new Set((platforms || []).filter((platform) => (
    platform === 'ios' || platform === 'android'
  )))] as SurveyMobilePlatform[];
  if (!selected.length || selected.length >= SURVEY_MOBILE_PLATFORM_OPTIONS.length) {
    return [];
  }
  return selected;
};

export const SURVEY_DEPLOYMENT_GROUP_OPTIONS: { value: SurveyDeploymentGroup; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'beta', label: 'Beta' },
  { value: 'internal', label: 'Internal Testing' },
];

export const normalizeDeploymentGroups = (groups?: SurveyDeploymentGroup[]) => {
  const selected = [...new Set((groups || []).filter((group) => (
    group === 'production' || group === 'beta' || group === 'internal'
  )))];
  return selected.length ? selected : ['production'] as SurveyDeploymentGroup[];
};

// Empty means every model. Unknown names are dropped.
export const normalizeDeviceModels = (models?: string[], catalog: string[] = []) => {
  const allowed = new Set(catalog);
  const selected = [...new Set((models || [])
    .map((model) => String(model || '').trim())
    .filter((model) => model && (!allowed.size || allowed.has(model))))];
  if (!selected.length) return [];
  if (catalog.length && selected.length >= catalog.length) return [];
  return selected;
};

export const emptySurveyQuestion = (): SurveyQuestion => ({
  prompt: '',
  type: 'text',
  choices: [''],
  additionalInfo: '',
  isDefault: false,
});

export const emptySurveyForm = (defaults: SurveyQuestion[] = []): SurveyFormState => ({
  name: '',
  summary: '',
  status: 'planning',
  deliveryMode: 'once',
  when: 'next_open',
  recurringDays: 30,
  audienceMode: 'device',
  deploymentGroups: ['production'],
  deviceModels: [],
  mobilePlatforms: [],
  maxResponses: '',
  maxImpressions: '',
  paceMode: 'all_at_once',
  perDay: '20',
  questions: defaults.map((question) => ({
    ...question,
    prompt: question.prompt || '',
    type: question.type || 'text',
    choices: question.choices?.length ? question.choices : [''],
    additionalInfo: question.additionalInfo || '',
    isDefault: Boolean(question.isDefault),
  })),
});

export const formFromSurvey = (survey: Survey): SurveyFormState => ({
  name: survey.name,
  summary: survey.summary || '',
  status: survey.status,
  deliveryMode: survey.deliveryMode || 'once',
  when: survey.when || 'next_open',
  recurringDays: normalizeRecurringDays(survey.recurringDays),
  audienceMode: survey.audienceMode === 'device' ? 'device' : 'account',
  deploymentGroups: normalizeDeploymentGroups(survey.deploymentGroups),
  deviceModels: normalizeDeviceModels(survey.deviceModels),
  mobilePlatforms: normalizeMobilePlatforms(survey.mobilePlatforms),
  maxResponses: survey.maxResponses ? String(survey.maxResponses) : '',
  maxImpressions: survey.maxImpressions ? String(survey.maxImpressions) : '',
  paceMode: survey.paceMode === 'per_day' ? 'per_day' : 'all_at_once',
  perDay: survey.perDay ? String(survey.perDay) : '20',
  questions: (survey.questions || []).map((question) => ({
    ...question,
    prompt: question.prompt || '',
    type: question.type || 'text',
    choices: question.choices?.length ? question.choices : [''],
    additionalInfo: question.additionalInfo || '',
    isDefault: Boolean(question.isDefault),
  })),
});

export const surveyStatusMeta = (status: SurveyStatus) =>
  SURVEY_STATUS_OPTIONS.find((option) => option.value === status) || SURVEY_STATUS_OPTIONS[0];

const STATUS_SORT_ORDER: Record<SurveyStatus, number> = {
  active: 0,
  paused: 1,
  planning: 2,
  closed: 3,
};

export const sortSurveys = (surveys: Survey[]) =>
  [...surveys].sort((a, b) => {
    const byStatus = (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99);
    if (byStatus !== 0) return byStatus;
    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
  });

export const surveySettingLines = (survey: Pick<
  Survey,
  'deliveryMode' | 'when' | 'recurringDays' | 'paceMode' | 'perDay' | 'audienceMode' | 'deploymentGroups' | 'deviceModels' | 'mobilePlatforms' | 'maxResponses' | 'maxImpressions'
>) => {
  const days = survey.recurringDays || 30;
  const when = survey.deliveryMode === 'recurring'
    ? [`Ask Every ${days} Days`]
    : [
        survey.when === 'after_open' ? `${days} Days After Next Open` : 'Next Open',
        'Once Only',
      ];

  const howMany: string[] = [];
  if (survey.maxResponses) howMany.push(`${survey.maxResponses} Max Responses`);
  if (survey.maxImpressions) howMany.push(`${survey.maxImpressions} Max Impressions`);
  if (!howMany.length) howMany.push('Unlimited');

  const byDevice = survey.audienceMode === 'device';
  const selected = normalizeDeploymentGroups(survey.deploymentGroups);
  const who = byDevice
    ? ['By Mobile Device ID']
    : SURVEY_DEPLOYMENT_GROUP_OPTIONS
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label);
  const devices = byDevice ? [] : (survey.deviceModels || []).filter(Boolean);
  const deviceLines = byDevice ? ['All installs'] : (devices.length ? devices : ['All Devices']);
  const platforms = normalizeMobilePlatforms(survey.mobilePlatforms);
  const platformLines = platforms.length
    ? platforms.map((platform) => (platform === 'ios' ? 'iOS' : 'Android'))
    : ['iOS & Android'];

  return {
    who,
    devices: deviceLines,
    platforms: platformLines,
    when,
    howMany,
    rollout: [survey.paceMode === 'per_day' ? `${survey.perDay || 20} Per Day` : 'All At Once'],
  };
};

export const parseOptionalPositiveInt = (value: string): number | null | undefined => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
};

export const SURVEY_YES_COLOR = '#03ac8f';
export const SURVEY_NO_COLOR = '#3b82f6';
export const SURVEY_SERIES_COLORS = ['#03ac8f', '#3b82f6', '#f59e0b', '#a970ff', '#6b7280', '#0ea5e9', '#84cc16', '#eb275c'];

export const surveyAnswerColor = (label: string, index = 0) => {
  const key = String(label || '').trim().toLowerCase();
  if (key === 'yes') return SURVEY_YES_COLOR;
  if (key === 'no') return SURVEY_NO_COLOR;
  return SURVEY_SERIES_COLORS[index % SURVEY_SERIES_COLORS.length];
};

export const excerpt = (value?: string, max = 160) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};
